<template>
  <div class="menu-page">
    <!-- Tab section: header + scroll horizontal -->
    <section class="menu-tabs-section">
      <div class="menu-tabs-header">
        <span class="menu-tabs-title">Categories</span>
      </div>
      <div class="menu-tabs-scroll">
        <div class="menu-tabs">
          <button
            v-for="cat in categories"
            :key="cat.id"
            type="button"
            class="menu-tab"
            :class="{ active: activeCategory === cat.id }"
            @click="activeCategory = cat.id"
          >
            {{ cat.label }}
          </button>
        </div>
      </div>
    </section>

    <!-- Tab content: full width screen -->
    <section class="menu-tab-content">
      <div class="menu-product-list">
        <p v-if="loadingProducts" class="menu-product-empty">Memuat produk...</p>
        <template v-else-if="products.length">
          <router-link
            v-for="product in products"
            :key="product.id"
            :to="'/foods/' + product.id"
            class="menu-product-item"
          >
            <div class="menu-product-img">
              <img :src="product.gambar" :alt="product.nama" />
            </div>
            <div class="menu-product-info">
              <div class="menu-product-row">
                <span class="menu-product-name">{{ product.nama }}</span>
                <span class="menu-product-price">{{ formatRupiah(product.harga) }}</span>
              </div>
              <p class="menu-product-desc">
                {{ product.deskripsi || "-" }}
              </p>
            </div>
          </router-link>
        </template>
        <p v-else class="menu-product-empty">
          Tidak ada produk di kategori ini.
        </p>
      </div>
    </section>
  </div>
</template>

<script>
import { api } from "@/utils/api";
import { formatRupiah } from "@/utils/formatRupiah";
import { savePersonal } from "@/utils/pesanan";

export default {
  name: "MenuItem",
  data() {
    return {
      categories: [],
      activeCategory: "all",
      products: [],
      loadingProducts: false,
    };
  },
  computed: {
    token() {
      try {
        const raw = localStorage.getItem("personal");
        const personal = raw ? JSON.parse(raw) : null;
        return (personal && personal.clientToken) || "";
      } catch {
        return "";
      }
    },
  },
  watch: {
    activeCategory() {
      this.fetchProducts();
    },
  },
  async mounted() {
    if (!this.token) {
      console.warn("Token tidak ditemukan di localStorage (personal.clientToken)");
      return;
    }
    try {
      const catRes = await api(`/categories/${this.token}`, { method: "GET" });
      const list = catRes.data && (Array.isArray(catRes.data) ? catRes.data : catRes.data.data || []);
      const clientId = (list || []).find((c) => c && c.client_id != null)?.client_id;
      if (clientId != null) {
        savePersonal({ clientId: Number(clientId) });
      }
      this.categories = [
        { id: "all", label: "Semua" },
        ...list.map((c) => ({
          id: c.id_cate ?? c.category_id,
          label: c.label ?? c.category_name ?? String(c.id ?? ""),
        })),
      ];
      await this.fetchProducts();
    } catch (err) {
      console.log("Gagal ambil categories", err);
    }
  },
  methods: {
    formatRupiah,
    async fetchProducts() {
      this.loadingProducts = true;
      try {
        if (this.activeCategory === "all") {
          const categoryIds = this.categories.filter((c) => c.id !== "all").map((c) => c.id);
          const results = await Promise.all(
            categoryIds.map((id) => api(`/categories/${id}/products`, { method: "GET" }))
          );
          const merged = results.flatMap((res) =>
            Array.isArray(res.data) ? res.data : res.data?.data ?? []
          );
          this.products = merged;
        } else {
          const res = await api(`/categories/${this.activeCategory}/products`, { method: "GET" });
          this.products = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
        }
      } catch (err) {
        console.log("Gagal ambil products", err);
        this.products = [];
      } finally {
        this.loadingProducts = false;
      }
    },
  },
};
</script>

<style scoped>
/* =========================
PAGE: full width screen
========================= */

.menu-page {
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
}

/* =========================
TAB SECTION: header + tabs scroll
========================= */

.menu-tabs-section {
  margin-top: 200px;
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 0;
  background: #fff;
  border-bottom: 1px solid #eee;
  padding: 12px 0 0;
}

.menu-tabs-header {
  padding: 0 16px 8px;
}

.menu-tabs-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
}

.menu-tabs-scroll {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 4px;
}

.menu-tabs-scroll::-webkit-scrollbar {
  height: 4px;
}

.menu-tabs {
  display: inline-flex;
  flex-wrap: nowrap;
  gap: 8px;
  padding: 0 16px 8px;
  width: max-content;
}

.menu-tab {
  flex: 0 0 auto;
  white-space: nowrap;
  border-radius: 20px;
  padding: 8px 16px;
  border: 1px solid #ddd;
  background: #fff;
  color: #333;
  font-size: 14px;
  cursor: pointer;
}

.menu-tab:hover {
  border-color: #0d6efd;
  color: #0d6efd;
}

.menu-tab.active {
  background: #0d6efd;
  color: #fff;
  border-color: #0d6efd;
}

/* =========================
TAB CONTENT: full width screen
========================= */

.menu-tab-content {
  width: 100%;
  max-width: 100%;
  padding: 16px;
  box-sizing: border-box;
}

.menu-product-list {
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.menu-product-empty {
  padding: 24px 12px;
  text-align: center;
  color: #777;
  font-size: 14px;
  margin: 0;
}

/* =========================
PRODUCT ITEM
========================= */

.menu-product-item {
  display: flex;
  gap: 12px;
  padding: 12px;
  border: 1px solid #eee;
  border-radius: 10px;
  text-decoration: none;
  color: inherit;
  width: 100%;
  box-sizing: border-box;
}

/* =========================
PRODUCT IMAGE
========================= */

.menu-product-img {
  width: 80px;
  height: 80px;
  min-width: 80px;
  overflow: hidden;
  border-radius: 8px;
}

.menu-product-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* =========================
PRODUCT INFO
========================= */

.menu-product-info {
  flex: 1;
  min-width: 0;
}

.menu-product-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.menu-product-name {
  font-weight: 600;
}

.menu-product-price {
  font-weight: 600;
  white-space: nowrap;
}

.menu-product-desc {
  font-size: 13px;
  color: #777;
  margin-top: 4px;
}
</style>
